import { type JSX, useState } from 'react';

import { MobiLauncherConfig } from '@schema/index.js';

import type { BuilderStore } from '../state.js';

import { TextField, ToggleField } from './Field.js';

export interface RemoteConfigEditorProps {
  store: BuilderStore;
}

const MIN_SECONDS = 60;
const MAX_SECONDS = 86_400;

/**
 * Remote-config polling editor (Enterprise feature: `remoteConfig.poll`).
 *
 * The runtime polls `endpoint`, sends an `If-None-Match` header when the
 * device has a cached ETag, and swaps configs on a successful Zod re-parse.
 * Schema floor on `pollIntervalSeconds` is 60s — we won't let an admin
 * configure a device fleet to hammer their own backend.
 *
 * The optional `config.remoteConfig` block is added/removed wholesale, the
 * same pattern as device + telemetry.
 */
export function RemoteConfigEditor({ store }: RemoteConfigEditorProps): JSX.Element {
  const remote = store.config.remoteConfig;

  if (remote === undefined) {
    return (
      <div className="editor">
        <header className="editor__head">
          <h2 className="editor__title">Remote config</h2>
          <p className="editor__sub">
            Poll an HTTPS endpoint for the live config — lets you change a
            fleet's launcher behavior without re-deploying.
          </p>
        </header>
        <fieldset className="editor__group">
          <legend className="editor__group-label">Remote config</legend>
          <p className="editor__hint">
            Not configured. Add the block to enable polling.
          </p>
          <button
            type="button"
            className="add-tile__btn"
            onClick={() =>
              store.patchRemoteConfig({
                enabled: false,
                pollIntervalSeconds: 300,
                useETag: true,
              })
            }
          >
            + Add remote config block
          </button>
        </fieldset>
      </div>
    );
  }

  function setInterval(raw: string): void {
    if (remote === undefined) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return;
    const clamped = Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, n));
    store.patchRemoteConfig({ ...remote, pollIntervalSeconds: clamped });
  }

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Remote config</h2>
        <p className="editor__sub">
          Failed polls (network, parse, signature) leave the existing config
          in place — the launcher does not fall back to defaults on a bad fetch.{' '}
          <a
            href="https://mobilauncher.com/docs/remote-config"
            target="_blank"
            rel="noopener noreferrer"
            className="editor__doc-link"
          >
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Endpoint</legend>
        <ToggleField
          label="Enabled"
          hint="Master switch; off ⇒ the launcher never polls, even with an endpoint set."
          value={remote.enabled}
          onChange={(v) => store.patchRemoteConfig({ ...remote, enabled: v })}
        />
        <TextField
          label="Endpoint URL"
          mono
          hint="HTTPS only. Empty ⇒ polling is a no-op even when enabled."
          value={remote.endpoint ?? ''}
          onChange={(v) => {
            if (v.length > 0) store.patchRemoteConfig({ ...remote, endpoint: v });
            else {
              const { endpoint: _o, ...rest } = remote;
              void _o;
              store.patchRemoteConfig(rest);
            }
          }}
        />
        <TestEndpointButton endpoint={remote.endpoint} />
        <button
          type="button"
          className="builder__btn builder__btn--ghost"
          onClick={() => store.patchRemoteConfig(undefined)}
          title="Remove the remote config block entirely"
        >
          Remove remote config block
        </button>
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Polling</legend>
        <label className="field field--inline">
          <span className="field__label">Interval (seconds)</span>
          <input
            type="number"
            className="field__input field__input--num"
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            step={60}
            value={remote.pollIntervalSeconds}
            onChange={(e) => setInterval(e.target.value)}
            style={{ width: '6rem' }}
          />
          <span className="field__hint">
            {MIN_SECONDS}–{MAX_SECONDS}s · {humanInterval(remote.pollIntervalSeconds)}
          </span>
        </label>
        <ToggleField
          label="Use ETag"
          hint="Send If-None-Match. Reduces bandwidth — only disable for endpoints that don't honor ETags."
          value={remote.useETag}
          onChange={(v) => store.patchRemoteConfig({ ...remote, useETag: v })}
        />
      </fieldset>
    </div>
  );
}

function humanInterval(seconds: number): string {
  if (seconds < 120) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86_400) return `${(seconds / 3600).toFixed(seconds % 3600 === 0 ? 0 : 1)}h`;
  return '1d';
}

/**
 * One-shot endpoint probe. Fires a GET (no If-None-Match) from the
 * browser, reports HTTP status, ETag, schema-parse outcome. CORS-bound:
 * the customer's endpoint must permit the Portal's origin for the
 * probe to succeed; we surface a hint when fetch throws (typical CORS
 * symptom). The launcher itself runs in WebView / managed-browser
 * context which doesn't enforce CORS, so a fetch failure here doesn't
 * guarantee on-device failure — it's a "probably needs CORS" signal.
 */
function TestEndpointButton({
  endpoint,
}: {
  endpoint: string | undefined;
}): JSX.Element {
  type Result =
    | { kind: 'ok'; status: number; etag: string | null; parse: 'valid' | 'invalid'; parseMsg?: string }
    | { kind: 'err'; message: string };
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(): Promise<void> {
    if (endpoint === undefined || endpoint.length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch(endpoint, { cache: 'no-store' });
      const etag = res.headers.get('etag');
      let parse: 'valid' | 'invalid' = 'invalid';
      let parseMsg: string | undefined;
      try {
        const body = await res.text();
        const parsed = MobiLauncherConfig.safeParse(JSON.parse(body));
        if (parsed.success) {
          parse = 'valid';
        } else {
          parseMsg = parsed.error.issues[0]?.message ?? 'schema error';
        }
      } catch (e) {
        parseMsg = e instanceof Error ? e.message : 'body parse failed';
      }
      setResult({ kind: 'ok', status: res.status, etag, parse, ...(parseMsg !== undefined && { parseMsg }) });
    } catch (e) {
      setResult({ kind: 'err', message: e instanceof Error ? e.message : 'fetch failed' });
    } finally {
      setBusy(false);
    }
  }

  const disabled = endpoint === undefined || endpoint.length === 0 || busy;

  return (
    <div className="field">
      <button
        type="button"
        className="add-tile__btn"
        onClick={() => void run()}
        disabled={disabled}
      >
        {busy ? 'Testing…' : 'Test endpoint'}
      </button>
      {result?.kind === 'ok' && (
        <p className="field__hint" style={{ marginTop: '0.5rem' }}>
          HTTP <strong>{result.status}</strong>
          {result.etag !== null && (
            <>
              {' · ETag '}
              <code>{result.etag}</code>
            </>
          )}
          {' · schema: '}
          {result.parse === 'valid' ? (
            <strong style={{ color: '#5eead4' }}>valid</strong>
          ) : (
            <>
              <strong style={{ color: '#fca5a5' }}>invalid</strong>
              {result.parseMsg !== undefined && ` — ${result.parseMsg}`}
            </>
          )}
        </p>
      )}
      {result?.kind === 'err' && (
        <p className="field__hint" style={{ marginTop: '0.5rem', color: '#fca5a5' }}>
          {result.message}
          {/* Browser TypeError on a cross-origin fetch is almost always
              a CORS rejection. The launcher itself runs in WebView and
              doesn't enforce CORS, so an on-device fetch may still
              succeed — this is a heads-up, not a verdict. */}
          {result.message.includes('Failed to fetch') &&
            ' — typically a CORS issue. On-device fetches from WebView don\'t enforce CORS, so the launcher itself may still succeed.'}
        </p>
      )}
    </div>
  );
}
